<template>
  <div>
    <!-- Stats Row -->
    <div class="row">
      <div class="col-lg-3">
        <div class="widget style1 navy-bg">
          <div class="row vertical-align">
            <div class="col-xs-3">
              <i class="fa fa-cubes fa-3x"></i>
            </div>
            <div class="col-xs-9 text-right">
              <span>{{ labels.configurations }}</span>
              <h2 class="font-bold">{{ configurations.length }}</h2>
            </div>
          </div>
        </div>
      </div>
      <div class="col-lg-3">
        <div class="widget style1 lazur-bg">
          <div class="row vertical-align">
            <div class="col-xs-3">
              <i class="fa fa-check-square-o fa-3x"></i>
            </div>
            <div class="col-xs-9 text-right">
              <span>{{ labels.selected }}</span>
              <h2 class="font-bold">{{ selectedIds.length }}</h2>
            </div>
          </div>
        </div>
      </div>
      <div class="col-lg-3">
        <div class="widget style1 yellow-bg">
          <div class="row vertical-align">
            <div class="col-xs-3">
              <i :class="{'fa-spin': runningCount > 0}" class="fa fa-spinner fa-3x"></i>
            </div>
            <div class="col-xs-9 text-right">
              <span>{{ labels.running }}</span>
              <h2 class="font-bold">{{ runningCount }}</h2>
            </div>
          </div>
        </div>
      </div>
      <div class="col-lg-3">
        <div class="widget style1 red-bg">
          <div class="row vertical-align">
            <div class="col-xs-3">
              <i class="fa fa-flag-checkered fa-3x"></i>
            </div>
            <div class="col-xs-9 text-right">
              <span>{{ labels.finished }}</span>
              <h2 class="font-bold">{{ finishedCount }}</h2>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="row">
      <!-- Main Content: Configurations & Table -->
      <div class="col-lg-8">
        <!-- Global Config (Compact) -->
        <div class="ibox float-e-margins">
          <div class="ibox-title">
            <h5>{{ labels.globalConfig }}</h5>
            <div class="ibox-tools">
              <a class="collapse-link">
                <i class="fa fa-chevron-up"></i>
              </a>
            </div>
          </div>
          <div class="ibox-content">
            <div class="row">
              <div class="col-md-3">
                <div class="form-group">
                  <label>{{ labels.nodeVersion }}</label>
                  <select v-model="globalConfig.node_version_id" class="form-control input-sm">
                    <option value="">{{ labels.defaultOption }}</option>
                    <option v-for="nv in nodeVersions" :key="nv.id" :value="nv.id">{{ nv.version }}</option>
                  </select>
                </div>
              </div>
              <div class="col-md-3">
                <div class="form-group">
                  <label>{{ labels.javaVersion }}</label>
                  <input v-model="globalConfig.java_version" class="form-control input-sm" placeholder="z.B. 21"
                         type="text">
                </div>
              </div>
              <div class="col-md-3">
                <div class="form-group">
                  <label>{{ labels.iosVersion }}</label>
                  <input v-model="globalConfig.ios_version" class="form-control input-sm" placeholder="z.B. 4.0.0"
                         type="text">
                </div>
              </div>
              <div class="col-md-3">
                <div class="form-group">
                  <label>{{ labels.androidVersion }}</label>
                  <input v-model="globalConfig.android_version" class="form-control input-sm" placeholder="z.B. 4.0.0"
                         type="text">
                </div>
              </div>
            </div>
            <div class="row">
              <div class="col-md-6">
                <div class="form-group">
                  <label>{{ labels.sourceRepository }}</label>
                  <select v-model="globalConfig.source_repository_id" class="form-control input-sm">
                    <option value="">{{ labels.defaultOption }}</option>
                    <option v-for="repo in repositories" :key="repo.id" :value="repo.id">{{ repo.name }} ({{
                        repo.slug
                      }})
                    </option>
                  </select>
                </div>
              </div>
              <div class="col-md-6">
                <div class="form-group">
                  <label>{{ labels.sourceBranch }}</label>
                  <select v-model="globalConfig.source_branch_id"
                          :disabled="!globalConfig.source_repository_id || branchesLoading"
                          class="form-control input-sm">
                    <option value="">{{ branchesLoading ? 'Wird geladen...' : labels.defaultOption }}</option>
                    <option v-for="branch in branches" :key="branch.id" :value="branch.id">{{ branch.name }}</option>
                  </select>
                  <span v-if="!globalConfig.source_repository_id" class="small text-muted">Wählen Sie zuerst ein Quell-Repository aus.</span>
                </div>
              </div>
            </div>
            <div class="row">
              <div class="col-md-12">
                <div class="form-group m-b-none">
                  <label>{{ labels.whatsNew }}</label>
                  <textarea v-model="globalConfig.whats_new" class="form-control input-sm" rows="2"></textarea>
                </div>
              </div>
            </div>
            <div v-if="includePublishing" class="row">
              <div class="col-md-6">
                <div class="form-group">
                  <label>{{ labels.iosPublishingType }}</label>
                  <select v-model="globalConfig.ios_publishing_type" class="form-control input-sm">
                    <option value="">{{ labels.defaultOption }}</option>
                    <option value="test_flight">TestFlight</option>
                    <option value="app_store">App Store</option>
                  </select>
                </div>
              </div>
              <div class="col-md-6">
                <div class="form-group">
                  <label>{{ labels.androidPublishingType }}</label>
                  <select v-model="globalConfig.android_publishing_type" class="form-control input-sm">
                    <option value="">{{ labels.defaultOption }}</option>
                    <option value="internal">Internal Track</option>
                    <option value="play_store">Play Store</option>
                  </select>
                </div>
              </div>
            </div>
            <hr>
            <div class="row">
              <div class="col-md-8">
                <label class="m-r-md cursor-pointer">
                  <input v-model="platforms.ios" type="checkbox"> iOS
                </label>
                <label class="m-r-md cursor-pointer">
                  <input v-model="platforms.android" type="checkbox"> Android
                </label>
                <label class="m-r-md cursor-pointer">
                  <input v-model="includePublishing" type="checkbox">
                  {{ labels.includePublishing }}
                </label>
              </div>
              <div class="col-md-4 text-right">
                <button :disabled="startDisabled" class="btn btn-primary" @click="startBuilds">
                  <i class="fa fa-play"></i> {{ labels.startBuilds }}
                </button>
              </div>
            </div>
          </div>
        </div>

        <!-- Selection Queue (moved here for better UX) -->
        <div class="ibox float-e-margins">
          <div class="ibox-title">
            <h5>{{ labels.selectedOrder }}</h5>
            <span class="label label-info pull-right">{{ selectedIds.length }}</span>
          </div>
          <div class="ibox-content p-none">
            <draggable v-model="selectedConfigsOrdered" class="list-group">
              <div v-for="config in selectedConfigsOrdered" :key="config.id" class="list-group-item"
                   style="cursor: move; border-left: 0; border-right: 0;">
                <i class="fa fa-bars text-muted m-r-xs"></i>
                <span class="small">{{ config.app_name }}</span>
                <button class="btn btn-xs btn-link pull-right text-danger" style="padding: 0;"
                        @click="removeFromSelection(config.id)">
                  <i class="fa fa-times"></i>
                </button>
              </div>
            </draggable>
            <div v-if="selectedConfigsOrdered.length === 0" class="text-muted text-center p-md small">
              {{ labels.noAppsSelected }}
            </div>
          </div>
        </div>

        <!-- Mass Update Table -->
        <div class="ibox float-e-margins">
          <div class="ibox-title">
            <h5>{{ labels.massUpdate }}</h5>
            <div class="ibox-tools">
              <span :class="{'mass-update-polling-pulse': pollTimer !== null}" class="label label-default">
                <i v-if="pollTimer !== null" class="fa fa-circle fa-xs m-r-xs"
                   style="font-size: 8px; vertical-align: middle;"></i>
                {{ labels.livePolling }}
              </span>
            </div>
          </div>
          <div class="ibox-content">
            <div v-if="loading" class="text-center m-b-md">
              <div class="sk-spinner sk-spinner-wave">
                <div class="sk-rect1"></div>
                <div class="sk-rect2"></div>
                <div class="sk-rect3"></div>
                <div class="sk-rect4"></div>
                <div class="sk-rect5"></div>
              </div>
              <div class="text-muted small m-t-sm">{{ labels.loading }}</div>
            </div>

            <div class="row" style="margin-bottom: 15px;">
              <div class="col-md-12">
                <div class="input-group">
                  <input v-model="searchQuery" :placeholder="labels.searchPlaceholder"
                         class="form-control input-sm"
                         type="text">
                  <span class="input-group-btn">
                                        <button class="btn btn-sm btn-primary" type="button">
                                            <i class="fa fa-search"></i>
                                        </button>
                                    </span>
                </div>
              </div>
            </div>

            <div v-if="errorMessage" class="alert alert-danger">
              {{ errorMessage }}
            </div>

            <div class="table-responsive">
              <table class="table table-hover table-striped">
                <thead>
                <tr>
                  <th style="width: 40px;">
                    <input :checked="allSelected" type="checkbox" @change="toggleSelectAll">
                  </th>
                  <th style="width: 60px;">{{ labels.id }}</th>
                  <th>{{ labels.internalName }}</th>
                  <th>{{ labels.appName }}</th>
                  <th>{{ labels.client }}</th>
                  <th style="width: 130px;">{{ labels.updatedAt }}</th>
                  <th>{{ labels.status }}</th>
                  <th class="text-right">{{ labels.actions }}</th>
                </tr>
                </thead>
                <tbody>
                <template v-for="config in paginatedConfigurations">
                  <tr :key="config.id">
                    <td>
                      <input v-model="selectedIds" :value="config.id" type="checkbox">
                    </td>
                    <td class="small text-muted font-bold">
                      #{{ config.id }}
                    </td>
                    <td>
                      <div :title="config.internal_name"
                           class="font-bold"
                           style="white-space: nowrap; overflow: hidden; text-overflow: ellipsis; max-width: 250px;">
                        {{ config.internal_name || '-' }}
                      </div>
                    </td>
                    <td>
                      <div class="font-bold">{{ config.app_name }}</div>
                      <div class="text-muted small">{{ config.bundle_id }}</div>
                    </td>
                    <td>
                      <div>{{ config.client_name || '-' }}</div>
                      <div class="text-muted small">{{ config.target_repository || '-' }}</div>
                    </td>
                    <td>
                      <div class="small text-muted" style="white-space: nowrap;">
                        {{ config.updated_at ? formatDate(config.updated_at) : '-' }}
                      </div>
                    </td>
                    <td>
                      <span v-if="currentBuild(config)" :class="statusBadgeClass(currentBuild(config).state)"
                            v-html="currentBuild(config).state_text"></span>
                      <span v-else class="label label-default">{{ labels.noBuild }}</span>
                    </td>
                    <td class="text-right">
                      <button class="btn btn-white btn-xs" @click="toggleLog(config)">
                        <i :class="showLog(config) ? 'fa-eye-slash' : 'fa-eye'" class="fa"></i>
                        {{ showLog(config) ? labels.hideLog : labels.showLog }}
                      </button>
                    </td>
                  </tr>
                  <tr v-if="showLog(config)" :key="`log-${config.id}`" class="bg-muted">
                    <td colspan="8">
                      <div class="p-xs">
                        <pre class="small"
                             style="white-space: pre-wrap; margin: 0; background: #2f4050; color: #fff; padding: 10px; border-radius: 4px;">{{
                            buildLog(config)
                          }}</pre>
                      </div>
                    </td>
                  </tr>
                </template>
                <tr v-if="!loading && filteredConfigurations.length === 0">
                  <td class="text-center text-muted p-md" colspan="8">
                    {{ labels.noResults }}
                  </td>
                </tr>
                </tbody>
              </table>
            </div>

            <!-- Pagination -->
            <div v-if="totalPages > 1" class="row m-t-md">
              <div class="col-sm-5">
                <div class="text-muted small p-t-xs">
                  {{ labels.showing }} {{ (currentPage - 1) * perPage + 1 }} -
                  {{ Math.min(currentPage * perPage, filteredConfigurations.length) }} {{ labels.of }}
                  {{ filteredConfigurations.length }} {{ labels.entries }}
                </div>
              </div>
              <div class="col-sm-7 text-right">
                <div class="btn-group">
                  <button :disabled="currentPage === 1" class="btn btn-white btn-sm" @click="currentPage = 1">
                    <i class="fa fa-angle-double-left"></i>
                  </button>
                  <button :disabled="currentPage === 1" class="btn btn-white btn-sm" @click="currentPage--">
                    <i class="fa fa-angle-left"></i>
                  </button>
                  <button v-for="page in pagesToShow" :key="page"
                          :class="currentPage === page ? 'btn-primary' : 'btn-white'"
                          class="btn btn-sm"
                          @click="currentPage = page">
                    {{ page }}
                  </button>
                  <button :disabled="currentPage === totalPages" class="btn btn-white btn-sm" @click="currentPage++">
                    <i class="fa fa-angle-right"></i>
                  </button>
                  <button :disabled="currentPage === totalPages" class="btn btn-white btn-sm"
                          @click="currentPage = totalPages">
                    <i class="fa fa-angle-double-right"></i>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Sidebar: Run Status, Progress, Queue & Logs -->
      <div class="col-lg-4">
        <!-- Mass Update Runs (Accordion) -->
        <div v-if="runs.length > 0" class="ibox float-e-margins" style="margin-bottom: 15px;">
          <div v-for="(runData, runIndex) in runs" :key="runData.run.id"
               :style="{
                         borderTop: runIndex > 0 ? '1px solid #e7eaec' : 'none',
                     }">
            <div class="ibox-title"
                 style="cursor: pointer; background: #f8f9fa; border-bottom: 1px solid #e7eaec; display: flex; justify-content: space-between; align-items: center; margin-bottom: 0;"
                 @click="toggleRun(runData.run.id)">
              <h5 style="margin: 0;">
                <i :class="openRuns[runData.run.id] ? 'fa-chevron-down' : 'fa-chevron-right'" class="fa m-r-xs"
                   style="width: 12px;"></i>
                <i :class="{'fa-pulse': runData.run.status === 'running'}" class="fa fa-tasks m-r-xs"></i>
                {{ labels.massUpdate }} &mdash;
                {{ runData.run.started_at ? formatDate(runData.run.started_at) : '-' }}
                <i v-if="runData.run.status === 'running'" class="fa fa-circle m-l-xs text-success"
                   style="font-size: 8px; vertical-align: middle; animation: blink 1.2s ease-in-out infinite;"></i>
              </h5>
              <div class="ibox-tools" style="float: none; margin: 0;">
                <span :class="getRunStatusClass(runData.run)" class="label">{{ formatRunStatus(runData.run) }}</span>
              </div>
            </div>
            <div v-show="openRuns[runData.run.id]" class="ibox-content" style="padding: 15px;">
              <div class="row m-b-sm">
                <div class="col-xs-6">
                  <small class="stats-label text-muted">Gestartet von</small>
                  <div class="font-bold small text-ellipsis"
                       style="overflow: hidden; text-overflow: ellipsis; white-space: nowrap;">{{
                      runData.run.started_by_name ? runData.run.started_by_name : (runData.run.started_by ? 'ID #' + runData.run.started_by : '-')
                    }}
                  </div>
                </div>
                <div class="col-xs-6 text-right">
                  <small class="stats-label text-muted">Gestartet am</small>
                  <div class="font-bold small">{{
                      runData.run.started_at ? formatDate(runData.run.started_at) : '-'
                    }}
                  </div>
                </div>
              </div>

              <div class="well well-sm m-t-sm"
                   style="background: #fff; border: 1px solid #e7eaec; margin-bottom: 0; padding: 10px;">
                <div class="row">
                  <div class="col-xs-12">
                    <small class="stats-label text-muted">Konfiguration</small>
                    <div class="m-t-xs" style="display: flex; flex-wrap: wrap; gap: 4px;">
                      <span v-if="runData.run.platforms && runData.run.platforms.includes('ios')"
                            class="label label-primary"><i
                          class="fa fa-apple"></i> iOS</span>
                      <span v-if="runData.run.platforms && runData.run.platforms.includes('android')"
                            class="label label-info"><i
                          class="fa fa-android"></i> Android</span>
                      <span v-if="runData.run.include_publishing" class="label label-warning"><i
                          class="fa fa-cloud-upload"></i> Publishing</span>
                    </div>

                    <div class="m-t-sm small">
                      <div v-if="getRunRepositoryName(runData.run)" :title="getRunRepositoryName(runData.run)"
                           class="text-ellipsis m-b-xs">
                        <i class="fa fa-git-square text-navy m-r-xs"></i> <strong>Repo:</strong>
                        {{ getRunRepositoryName(runData.run) }}
                      </div>
                      <div v-if="getRunBranchName(runData.run)" class="text-ellipsis m-b-xs">
                        <i class="fa fa-code-fork text-navy m-r-xs"></i> <strong>Branch:</strong>
                        {{ getRunBranchName(runData.run) }}
                      </div>
                      <div class="row">
                        <div v-if="getRunNodeVersionName(runData.run)" class="col-xs-6">
                          <i class="fa fa-code text-navy m-r-xs"></i> <strong>Node:</strong>
                          {{ getRunNodeVersionName(runData.run) }}
                        </div>
                        <div v-if="runData.run.java_version" class="col-xs-6">
                          <i class="fa fa-coffee text-navy m-r-xs"></i> <strong>Java:</strong>
                          {{ runData.run.java_version }}
                        </div>
                      </div>
                      <div class="row m-t-xs">
                        <div v-if="runData.run.ios_version" class="col-xs-6">
                          <i class="fa fa-apple text-navy m-r-xs"></i> <strong>iOS v:</strong>
                          {{ runData.run.ios_version }}
                        </div>
                        <div v-if="runData.run.android_version" class="col-xs-6">
                          <i class="fa fa-android text-navy m-r-xs"></i> <strong>Android v:</strong>
                          {{ runData.run.android_version }}
                        </div>
                      </div>
                      <div v-if="runData.run.whats_new" class="m-t-xs" style="border-top: 1px dashed #eee; pt-xs">
                        <i class="fa fa-list-alt text-navy m-r-xs"></i> <strong>Neues:</strong>
                        <span class="text-muted italic">{{ runData.run.whats_new }}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div class="m-t-md">
                <div class="row">
                  <div class="col-xs-6">
                    <small class="stats-label">Fortschritt</small>
                    <h4 class="no-margins font-bold">{{ runData.run.completed_builds || 0 }} /
                      {{ runData.run.total_builds || 0 }} Apps</h4>
                  </div>
                  <div class="col-xs-6 text-right">
                    <small class="stats-label">Gesamtfortschritt</small>
                    <h4 class="no-margins font-bold text-navy">{{ getRunProgress(runData) }}%</h4>
                  </div>
                </div>
                <div class="progress progress-mini m-t-xs">
                  <div
                      :class="['progress-bar', runData.run.status === 'stopped' ? 'progress-bar-warning' : 'progress-bar-navy']"
                      :style="{width: getRunProgress(runData) + '%'}"></div>
                </div>

                <!-- Queue Status List -->
                <div v-if="runData.builds && runData.builds.length > 0" class="m-t-md"
                     style="max-height: 400px; overflow-y: auto; border: 1px solid #e7eaec; border-radius: 4px; background: #fff;">
                  <ul class="list-group clear-list no-margins">
                    <li v-for="(build, index) in runData.builds" :key="build.id"
                        :style="index === runData.builds.length - 1 ? 'border-bottom: none;' : ''"
                        class="list-group-item"
                        style="padding: 12px 15px; border-left: none; border-right: none;">
                      <div class="m-b-xs"
                           style="display: flex; justify-content: space-between; align-items: center; width: 100%;">
                        <div :class="{'text-muted': isWaitingBuild(build)}"
                             class="small font-bold"
                             style="white-space: nowrap; overflow: hidden; text-overflow: ellipsis; display: flex; align-items: center; gap: 5px;">
                          <span style="overflow: hidden; text-overflow: ellipsis;">{{ index + 1 }}. {{ getAppName(build.app_configuration_id) }}</span>
                          <span class="text-muted font-normal" style="font-size: 10px; flex-shrink: 0; display: flex; align-items: center; gap: 4px;">
                            <span style="background: #f3f3f4; padding: 1px 4px; border-radius: 2px;">
                              App ID: {{ getAppId(build.app_configuration_id) }} | Config ID: {{ build.app_configuration_id }}
                            </span>
                            <a :href="getConfigEditLink(build.app_configuration_id)" target="_blank" title="Konfiguration bearbeiten" class="text-navy">
                              <i class="fa fa-external-link"></i>
                            </a>
                          </span>
                        </div>
                        <div class="small">
                          <span v-if="build.state === 9" class="text-success"><i class="fa fa-check-circle"></i></span>
                          <span v-else-if="isFailed(build.state)" class="text-danger"><i class="fa fa-times-circle"></i></span>
                          <span v-else-if="build.state === 10" class="text-warning"><i class="fa fa-ban"></i></span>
                          <span v-else-if="isActiveBuild(build, runData.run)" class="text-primary"><i
                              class="fa fa-circle-o-notch fa-spin"></i></span>
                          <span v-else-if="isWaitingBuild(build)" class="text-muted"><i
                              class="fa fa-clock-o"></i></span>
                        </div>
                      </div>

                      <div style="display: flex; align-items: center; gap: 8px;">
                        <!-- Step 1: Sync -->
                        <div style="flex: 1; display: flex; align-items: center; white-space: nowrap;">
                          <i :class="getStepIcon(build, 'sync')" class="m-r-xs"></i>
                          <small :class="getStepTextClass(build, 'sync')">Repo Sync</small>
                        </div>
                        <!-- Divider -->
                        <div style="color: #ccc;"><i class="fa fa-angle-right"></i></div>
                        <!-- Step 2: Build -->
                        <div
                            style="flex: 2; display: flex; align-items: center; white-space: nowrap; overflow: hidden; text-overflow: ellipsis;">
                          <i :class="getStepIcon(build, 'build')" class="m-r-xs"></i>
                          <small :class="getStepTextClass(build, 'build')">
                            {{ getBuildPhaseText(build) }}
                          </small>
                        </div>
                      </div>

                      <div v-if="isActiveBuild(build, runData.run)" class="progress progress-mini m-t-xs">
                        <div :class="build.state === 15 ? 'progress-bar-info' : 'progress-bar-primary'"
                             :style="{width: build.progress + '%'}"
                             class="progress-bar progress-bar-striped active"></div>
                      </div>
                    </li>
                  </ul>
                </div>
              </div>

              <div v-if="runData.run.status === 'running'" class="m-t-md">
                <button class="btn btn-outline btn-danger btn-block btn-sm" @click="stopCurrentRun">
                  <i class="fa fa-stop m-r-xs"></i> Massenupdate stoppen
                </button>
              </div>
              <div v-else-if="runData.run.status === 'finished'" class="m-t-md text-center">
                <span class="text-success small font-bold"><i
                    class="fa fa-check-circle m-r-xs"></i> Abgeschlossen</span>
              </div>
              <div v-else-if="runData.run.status === 'stopped'" class="m-t-md text-center">
                <span class="text-warning small font-bold"><i class="fa fa-hand-stop-o m-r-xs"></i> Gestoppt</span>
              </div>
            </div>
          </div>
        </div><!-- end accordion group -->

        <div v-if="runs.length === 0" class="text-center m-t-sm">
          <div class="well well-sm">
            <i class="fa fa-info-circle"></i> {{ labels.noBuild }}
          </div>
        </div>

        <!-- Active Run Summary Strip (only shown when a run exists) -->
        <div v-if="runs.length > 0 && runs[0]" class="ibox float-e-margins">
          <div class="ibox-content" style="padding: 12px 15px;">
            <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 8px;">
              <span class="small font-bold text-muted" style="text-transform: uppercase; letter-spacing: 0.05em;">
                Letzter Run
              </span>
              <span class="text-muted small">
                <i :class="{'fa-spin': pollTimer !== null}" class="fa fa-refresh m-r-xs" style="font-size: 10px;"></i>
                alle {{ pollIntervalMs / 1000 }}s
              </span>
            </div>

            <!-- Three counters scoped to the latest run's builds -->
            <div class="row" style="margin-bottom: 10px;">
              <div class="col-xs-4 text-center">
                <div :class="currentRunFinishedCount > 0 ? 'text-navy' : 'text-muted'" class="font-bold"
                     style="font-size: 22px; line-height: 1;">
                  {{ currentRunFinishedCount }}
                </div>
                <small class="text-muted" style="font-size: 10px; text-transform: uppercase;">Fertig</small>
              </div>
              <div class="col-xs-4 text-center">
                <div :class="currentRunRunningCount > 0 ? 'text-warning' : 'text-muted'" class="font-bold"
                     style="font-size: 22px; line-height: 1;">
                  {{ currentRunRunningCount }}
                </div>
                <small class="text-muted" style="font-size: 10px; text-transform: uppercase;">Aktiv</small>
              </div>
              <div class="col-xs-4 text-center">
                <div :class="currentRunFailedCount > 0 ? 'text-danger' : 'text-muted'" class="font-bold"
                     style="font-size: 22px; line-height: 1;">
                  {{ currentRunFailedCount }}
                </div>
                <small class="text-muted" style="font-size: 10px; text-transform: uppercase;">Fehler</small>
              </div>
            </div>

            <!-- Run progress bar with segmented colours -->
            <div style="position: relative; height: 8px; background: #e7eaec; border-radius: 4px; overflow: hidden;">
              <!-- Finished segment (navy) -->
              <div :style="{ width: currentRunFinishedPercent + '%' }"
                   style="position: absolute; left: 0; top: 0; height: 100%; background: #1a7bb9; transition: width 0.4s ease;"></div>
              <!-- Running segment (amber) stacked after finished -->
              <div :style="{ width: currentRunRunningPercent + '%', left: currentRunFinishedPercent + '%' }"
                   style="position: absolute; top: 0; height: 100%; background: #f8ac59; transition: width 0.4s ease, left 0.4s ease;"></div>
              <!-- Failed segment (red) stacked after finished+running -->
              <div
                  :style="{ width: currentRunFailedPercent + '%', left: (currentRunFinishedPercent + currentRunRunningPercent) + '%' }"
                  style="position: absolute; top: 0; height: 100%; background: #ed5565; transition: width 0.4s ease, left 0.4s ease;"></div>
            </div>
            <div style="display: flex; justify-content: space-between; margin-top: 4px;">
              <small class="text-muted" style="font-size: 10px;">
                {{ currentRunTotalBuilds > 0 ? (currentRunFinishedCount + currentRunFailedCount) : 0 }} /
                {{ currentRunTotalBuilds }} abgeschlossen
              </small>
              <small class="text-muted" style="font-size: 10px;">{{ currentRunOverallPercent }}%</small>
            </div>
          </div>
        </div>

        <!-- Failed Builds Summary -->
        <div class="ibox float-e-margins">
          <div class="ibox-title">
            <h5><i class="fa fa-exclamation-triangle text-danger m-r-xs"></i> Fehlgeschlagene Builds</h5>
            <span class="label label-danger pull-right">{{ failedBuildsAcrossRuns.length }}</span>
          </div>
          <div class="ibox-content p-none">
            <ul v-if="failedBuildsAcrossRuns.length > 0" class="list-group clear-list no-margins">
              <li v-for="entry in paginatedFailedBuilds" :key="entry.buildId" class="list-group-item"
                  style="padding: 10px 15px;">
                <div style="display: flex; justify-content: space-between; align-items: flex-start;">
                  <div style="flex: 1; min-width: 0;">
                    <div class="small font-bold"
                         style="white-space: nowrap; overflow: hidden; text-overflow: ellipsis;">
                      {{ entry.appName }}
                    </div>
                    <div class="m-t-xs" style="display: flex; align-items: center; gap: 6px; flex-wrap: wrap;">
                      <span class="label label-danger" style="font-size: 10px;">
                        <i :class="entry.failedAtSync ? 'fa fa-refresh' : 'fa fa-cloud'" class="m-r-xs"></i>
                        {{ entry.failedAtSync ? 'Repo Sync' : 'Codemagic' }}
                      </span>
                      <span class="text-muted" style="font-size: 10px;">{{ entry.runDate }}</span>
                    </div>
                  </div>
                  <a v-if="entry.codemagicBuildId"
                     :href="'https://codemagic.io/app/' + entry.codemagicBuildId + '/build/' + entry.codemagicBuildId"
                     class="btn btn-xs btn-white m-l-xs" style="flex-shrink: 0;" target="_blank"
                     title="In Codemagic öffnen">
                    <i class="fa fa-external-link"></i>
                  </a>
                </div>
              </li>
            </ul>
            <div v-if="failedBuildsAcrossRuns.length > 0 && failedTotalPages > 1"
                 style="padding: 8px 15px; border-top: 1px solid #e7eaec; display: flex; justify-content: space-between; align-items: center;">
              <span class="text-muted small">
                {{
                  (failedBuildsPage - 1) * perPageFailed + 1
                }}–{{ Math.min(failedBuildsPage * perPageFailed, failedBuildsAcrossRuns.length) }}
                von {{ failedBuildsAcrossRuns.length }}
              </span>
              <div class="btn-group">
                <button :disabled="failedBuildsPage === 1" class="btn btn-white btn-xs" @click="failedBuildsPage--">
                  <i class="fa fa-angle-left"></i>
                </button>
                <button v-for="p in failedTotalPages" :key="p"
                        :class="failedBuildsPage === p ? 'btn-primary' : 'btn-white'"
                        class="btn btn-xs"
                        @click="failedBuildsPage = p">
                  {{ p }}
                </button>
                <button :disabled="failedBuildsPage === failedTotalPages" class="btn btn-white btn-xs"
                        @click="failedBuildsPage++">
                  <i class="fa fa-angle-right"></i>
                </button>
              </div>
            </div>
            <div v-if="failedBuildsAcrossRuns.length === 0" class="text-muted text-center p-md small">
              <i class="fa fa-check-circle text-success m-r-xs"></i> Keine fehlgeschlagenen Builds.
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import draggable from 'vuedraggable';

export default {
  components: {
    draggable,
  },
  data() {
    return {
      runId: null,
      run: null,
      configurations: [],
      loading: true,
      selectedIds: [],
      platforms: {
        ios: true,
        android: true,
      },
      includePublishing: false,
      nodeVersions: [],
      repositories: [],
      branches: [],
      branchesLoading: false,
      globalConfig: {
        node_version_id: '',
        java_version: '',
        source_repository_id: '',
        source_branch_id: '',
        ios_version: '',
        android_version: '',
        ios_publishing_type: '',
        android_publishing_type: '',
        whats_new: '',
      },
      errorMessage: '',
      runs: [],
      openRuns: {},
      pollTimer: null,
      pollIntervalMs: 5000,
      logOpen: {},
      searchQuery: '',
      currentPage: 1,
      perPage: 15,
      failedBuildsPage: 1,
      perPageFailed: 5,
      labels: {
        massUpdate: 'Massenupdate',
        livePolling: 'Live-Abfrage',
        loading: 'Konfigurationen werden geladen...',
        total: 'Gesamt',
        selected: 'Ausgewählt',
        running: 'Wird ausgeführt',
        finished: 'Abgeschlossen',
        failed: 'Fehlgeschlagen',
        configurations: 'Konfigurationen',
        totalConfigurations: 'Gesamtkonfigurationen',
        selectedConfigurations: 'Ausgewählte Konfigurationen',
        inProgress: 'Builds werden ausgeführt',
        finishedBuilds: 'Abgeschlossene Builds',
        includePublishing: 'Veröffentlichung einbeziehen',
        startBuilds: 'Massenupdate starten',
        appName: 'App-Name',
        client: 'Client',
        id: 'ID',
        internalName: 'Interner Name',
        updatedAt: 'Aktualisiert',
        bundleId: 'Bundle-ID',
        repository: 'Repository',
        status: 'Status',
        actions: 'Aktionen',
        showLog: 'Log anzeigen',
        hideLog: 'Log ausblenden',
        noLog: 'Noch kein Log verfügbar.',
        noBuild: 'Noch keine Builds',
        searchPlaceholder: 'Nach App, Client, Bundle oder Repository suchen...',
        noResults: 'Keine Konfigurationen gefunden, die Ihrer Suche entsprechen.',
        globalConfig: 'Globale Konfiguration für dieses Massenupdate',
        nodeVersion: 'Node-Version',
        javaVersion: 'Java-Version',
        sourceRepository: 'Quell-Repository',
        sourceBranch: 'Quell-Branch',
        iosVersion: 'iOS-Version',
        androidVersion: 'Android-Version',
        iosPublishingType: 'iOS Veröffentlichungstyp',
        androidPublishingType: 'Android Veröffentlichungstyp',
        whatsNew: 'Neues in dieser Version',
        defaultOption: 'Standard (aus App-Konfig)',
        selectedOrder: 'Reihenfolge der ausgewählten Apps (Drag & Drop zum Sortieren)',
        noAppsSelected: 'Keine Apps ausgewählt. Wählen Sie Apps aus der Liste unten aus.',
        showing: 'Zeige',
        of: 'von',
        entries: 'Einträgen',
      },
    };
  },
  computed: {
    selectedConfigsOrdered: {
      get() {
        return this.selectedIds.map(id => this.configurations.find(c => c.id === id)).filter(Boolean);
      },
      set(newList) {
        this.selectedIds = newList.map(c => c.id);
      }
    },
    runProgress() {
      return this.getRunProgress(this.run);
    },
    allSelected() {
      return this.configurations.length > 0 && this.selectedIds.length === this.configurations.length;
    },
    startDisabled() {
      return this.selectedIds.length === 0 || (!this.platforms.ios && !this.platforms.android);
    },
    filteredConfigurations() {
      const query = this.searchQuery.trim().toLowerCase();
      if (!query) {
        return this.configurations;
      }
      return this.configurations.filter((config) => {
        const haystack = [
          config.app_name,
          config.internal_name,
          config.client_name,
          config.bundle_id,
          config.target_repository,
        ].filter(Boolean).join(' ').toLowerCase();
        return haystack.includes(query);
      });
    },
    totalBuildsCount() {
      return this.configurations.filter((config) => !!this.currentBuild(config)).length;
    },
    runningCount() {
      return this.configurations.filter((config) => {
        const build = this.currentBuild(config);
        return build && this.isRunning(build.state);
      }).length;
    },
    finishedCount() {
      return this.configurations.filter((config) => {
        const build = this.currentBuild(config);
        return build && this.isFinished(build.state);
      }).length;
    },
    failedCount() {
      return this.configurations.filter((config) => {
        const build = this.currentBuild(config);
        return build && this.isFailed(build.state);
      }).length;
    },
    runningPercent() {
      if (!this.totalBuildsCount) return 0;
      return Math.round((this.runningCount / this.totalBuildsCount) * 100);
    },
    finishedPercent() {
      if (!this.totalBuildsCount) return 0;
      return Math.round((this.finishedCount / this.totalBuildsCount) * 100);
    },
    failedPercent() {
      if (!this.totalBuildsCount) return 0;
      return Math.round((this.failedCount / this.totalBuildsCount) * 100);
    },
    // --- Current run stats (scoped to runs[0].builds, not the global config list) ---
    currentRunBuilds() {
      return (this.runs.length > 0 && this.runs[0].builds) ? this.runs[0].builds : [];
    },
    currentRunTotalBuilds() {
      return this.currentRunBuilds.length;
    },
    currentRunFinishedCount() {
      return this.currentRunBuilds.filter(b => b.state === 9).length;
    },
    currentRunRunningCount() {
      return this.currentRunBuilds.filter(b => this.isRunning(b.state)).length;
    },
    currentRunFailedCount() {
      return this.currentRunBuilds.filter(b => this.isFailed(b.state) || b.state === 10).length;
    },
    currentRunFinishedPercent() {
      if (!this.currentRunTotalBuilds) return 0;
      return Math.round((this.currentRunFinishedCount / this.currentRunTotalBuilds) * 100);
    },
    currentRunRunningPercent() {
      if (!this.currentRunTotalBuilds) return 0;
      return Math.round((this.currentRunRunningCount / this.currentRunTotalBuilds) * 100);
    },
    currentRunFailedPercent() {
      if (!this.currentRunTotalBuilds) return 0;
      return Math.round((this.currentRunFailedCount / this.currentRunTotalBuilds) * 100);
    },
    currentRunOverallPercent() {
      if (!this.currentRunTotalBuilds) return 0;
      const done = this.currentRunFinishedCount + this.currentRunFailedCount;
      return Math.round((done / this.currentRunTotalBuilds) * 100);
    },
    // -------------------------------------------------------------------------------
    failedBuildsAcrossRuns() {
      const failed = [];
      this.runs.forEach((runData) => {
        if (!runData.builds) return;
        runData.builds.forEach((build) => {
          if (!this.isFailed(build.state) && build.state !== 10) return;
          const failedAtSync = build.state >= 11 && (build.progress || 0) < 25;
          failed.push({
            buildId: build.id,
            appName: this.getAppName(build.app_configuration_id),
            failedAtSync,
            codemagicBuildId: build.codemagic_build_id || null,
            runDate: runData.run.started_at ? this.formatDate(runData.run.started_at) : '-',
          });
        });
      });
      return failed;
    },
    paginatedFailedBuilds() {
      const start = (this.failedBuildsPage - 1) * this.perPageFailed;
      return this.failedBuildsAcrossRuns.slice(start, start + this.perPageFailed);
    },
    failedTotalPages() {
      return Math.max(1, Math.ceil(this.failedBuildsAcrossRuns.length / this.perPageFailed));
    },
    paginatedConfigurations() {
      const start = (this.currentPage - 1) * this.perPage;
      const end = start + this.perPage;
      return this.filteredConfigurations.slice(start, end);
    },
    totalPages() {
      return Math.ceil(this.filteredConfigurations.length / this.perPage);
    },
    pagesToShow() {
      const range = [];
      for (let i = 1; i <= this.totalPages; i++) {
        if (i === 1 || i === this.totalPages || Math.abs(i - this.currentPage) <= 2) {
          range.push(i);
        }
      }
      return range;
    },
  },
  watch: {
    searchQuery() {
      this.currentPage = 1;
    },
    failedTotalPages(newVal) {
      if (this.failedBuildsPage > newVal) {
        this.failedBuildsPage = newVal;
      }
    },
    'globalConfig.source_repository_id': function (newVal) {
      this.fetchBranches(newVal);
      this.globalConfig.source_branch_id = '';
    }
  },
  created() {
    if (window.Laravel && window.Laravel.csrfToken) {
      window.axios.defaults.headers.common['X-CSRF-TOKEN'] = window.Laravel.csrfToken;
    }
    // fetch configurations (metadata) first and then current run
    this.fetchConfigurations().then(() => this.fetchCurrentRun());
  },
  beforeDestroy() {
    if (this.pollTimer) {
      clearInterval(this.pollTimer);
    }
  },
  methods: {
    formatDate(dateString) {
      if (!dateString) return '-';
      const date = new Date(dateString);
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      return `${day}.${month}.${year} ${hours}:${minutes}`;
    },
    formatRunStatus(run) {
      if (!run) return 'NICHT AKTIV';
      if (run.status === 'running') return 'WIRD AUSGEFÜHRT';
      if (run.status === 'finished') return 'ABGESCHLOSSEN';
      if (run.status === 'stopped') return 'GESTOPPT';
      if (run.status === 'failed') return 'FEHLGESCHLAGEN';
      return run.status ? run.status.toUpperCase() : 'NICHT AKTIV';
    },
    getRunStatusClass(run) {
      if (!run) return 'text-muted';
      if (run.status === 'running') return 'text-success font-bold';
      if (run.status === 'finished') return 'text-success font-bold';
      if (run.status === 'stopped') return 'text-warning font-bold'; // Orange/yellow for stopped
      if (run.status === 'failed') return 'text-danger font-bold';
      return 'text-muted';
    },
    getAppName(configId) {
      const config = this.configurations.find(c => c.id === configId);
      return config ? config.app_name : 'Unbekannt';
    },
    getAppId(configId) {
      const config = this.configurations.find(c => c.id === configId);
      return config ? config.app_id : '-';
    },
    getConfigEditLink(configId) {
      return `/backend/app_configurations/${configId}/edit`;
    },
    getActiveBuild() {
      if (!this.run || !this.run.builds) return null;
      return this.run.builds.find(b => b.state === 15 || (b.state >= 1 && b.state <= 8));
    },
    isActiveBuild(build, run) {
      if (!run || run.status !== 'running') return false;
      // A build is active if it's currently syncing or building on Codemagic
      return build.state === 15 || (build.state >= 1 && build.state <= 8);
    },
    isWaitingBuild(build) {
      return build.state === 0;
    },
    getStepIcon(build, step) {
      const state = build.state;
      if (step === 'sync') {
        if (state === 15) return 'fa fa-refresh fa-spin text-primary';
        if ((state >= 1 && state <= 9) || (state >= 11 && build.progress >= 25)) return 'fa fa-check text-success';
        if (state >= 11 && build.progress < 25) return 'fa fa-times text-danger';
        return 'fa fa-circle-o text-muted';
      }
      if (step === 'build') {
        if (state >= 1 && state <= 8) return 'fa fa-circle-o-notch fa-spin text-primary';
        if (state === 9) return 'fa fa-check text-success';
        if (state >= 11 && build.progress >= 25) return 'fa fa-times text-danger';
        if (state === 10) return 'fa fa-ban text-warning';
        return 'fa fa-circle-o text-muted';
      }
    },
    getStepTextClass(build, step) {
      const state = build.state;
      if (step === 'sync') {
        if (state === 15) return 'text-primary font-bold';
        if ((state >= 1 && state <= 9) || (state >= 11 && build.progress >= 25)) return 'text-success';
        return 'text-muted';
      }
      if (step === 'build') {
        if (state >= 1 && state <= 8) return 'text-primary font-bold';
        if (state === 9) return 'text-success';
        if (state >= 11 && build.progress >= 25) return 'text-danger';
        return 'text-muted';
      }
    },
    getBuildPhaseText(build) {
      const states = {
        1: 'Codemagic: Starting',
        2: 'Codemagic: Queued',
        3: 'Codemagic: Preparing',
        4: 'Codemagic: Fetching',
        5: 'Codemagic: Building',
        6: 'Codemagic: Testing',
        7: 'Codemagic: Publishing',
        8: 'Codemagic: Finishing',
        9: 'Codemagic: Finished',
        11: 'Codemagic: Failed',
        12: 'Codemagic: Failed',
        13: 'Codemagic: Failed',
        14: 'Codemagic: Failed',
      };
      if (build.state >= 1 && build.state <= 14 && build.state !== 10) {
        return states[build.state] || 'Codemagic Build';
      }
      return 'Codemagic Build';
    },
    getBuildQueueStatusText(build) {
      const states = {
        0: 'Wartet...',
        1: 'Codemagic: Startet',
        2: 'Codemagic: Queued',
        3: 'Codemagic: Preparing',
        4: 'Codemagic: Fetching',
        5: 'Codemagic: Building',
        6: 'Codemagic: Testing',
        7: 'Codemagic: Publishing',
        8: 'Codemagic: Finishing',
        9: 'Abgeschlossen',
        10: 'Abgebrochen',
        11: 'Fehlgeschlagen',
        15: 'Synchronisierung...',
      };
      return states[build.state] || 'Unbekannt';
    },
    getBuildQueueStatusClass(build) {
      if (build.state === 9) return 'text-success';
      if (build.state === 10) return 'text-warning';
      if (build.state >= 11) return 'text-danger';
      if (this.isActiveBuild(build)) return 'text-primary font-bold';
      return 'text-muted';
    },
    fetchBranches(repoId) {
      this.branches = [];
      if (!repoId) {
        return;
      }

      this.branchesLoading = true;
      window.axios.get(`/backend/mass-update/branches/${repoId}`)
          .then(response => {
            this.branches = response.data;
          })
          .catch(error => {
            console.error('Failed to fetch branches:', error);
          })
          .finally(() => {
            this.branchesLoading = false;
          });
    },
    fetchConfigurations() {
      this.loading = true;
      return window.axios.get('/backend/mass-update/data')
          .then((response) => {
            this.configurations = response.data.configurations.map((config) => {
              if (config.last_build) {
                config.current_build_id = config.last_build.id;
                config.current_build = config.last_build;
              } else {
                config.current_build_id = null;
                config.current_build = null;
              }
              return config;
            });
            this.nodeVersions = response.data.node_versions || [];
            this.repositories = response.data.repositories || [];
            this.loading = false;
            this.startPolling();
          })
          .catch((error) => {
            this.errorMessage = error.response?.data?.message || error.message;
            this.loading = false;
          });
    },
    toggleSelectAll(event) {
      if (event.target.checked) {
        this.selectedIds = this.configurations.map((config) => config.id);
      } else {
        this.selectedIds = [];
      }
    },
    startBuilds() {
      this.errorMessage = '';
      if (this.selectedIds.length === 0) {
        this.errorMessage = 'Bitte wählen Sie mindestens eine Konfiguration aus.';
        return;
      }
      if (!this.platforms.ios && !this.platforms.android) {
        this.errorMessage = 'Bitte wählen Sie mindestens eine Plattform aus.';
        return;
      }

      const platforms = [];
      if (this.platforms.ios) platforms.push('ios');
      if (this.platforms.android) platforms.push('android');

      window.axios.post('/backend/mass-update/start', {
        configuration_ids: this.selectedIds,
        platforms,
        include_publishing: this.includePublishing,
        ...this.globalConfig,
      }).then((response) => {
        const builds = response.data.builds || [];
        const runId = response.data.mass_update_run_id || null;
        if (runId) {
          this.runId = runId;
          this.run = {
            id: runId,
            status: 'running',
            builds: builds,
            total_builds: builds.length,
            completed_builds: 0,
            started_at: new Date().toISOString(),
            platforms: platforms,
            include_publishing: this.includePublishing,
            ...this.globalConfig,
          };

          // Add to runs list and open it
          this.runs.unshift({
            run: this.run,
            builds: builds
          });
          this.$set(this.openRuns, runId, true);

          // Keep only last 5
          if (this.runs.length > 5) {
            this.runs = this.runs.slice(0, 5);
          }
        }
        builds.forEach((build) => {
          const config = this.configurations.find((c) => c.id === build.app_configuration_id);
          if (config) {
            config.current_build_id = build.id;
            config.current_build = build;
          }
        });
        this.startPolling();
      }).catch((error) => {
        this.errorMessage = error.response?.data?.message || error.message;
      });
    },
    startPolling() {
      if (this.pollTimer) {
        clearInterval(this.pollTimer);
      }
      this.pollTimer = setInterval(this.pollStatuses, this.pollIntervalMs);
      this.pollStatuses();
    },
    stopPolling() {
      if (this.pollTimer) {
        clearInterval(this.pollTimer);
        this.pollTimer = null;
      }
    },
    removeFromSelection(id) {
      this.selectedIds = this.selectedIds.filter(selectedId => selectedId !== id);
    },

    fetchCurrentRun() {
      return window.axios.get('/backend/mass-update/run/current')
          .then((response) => {
            this.runs = response.data.runs || [];
            if (this.runs.length > 0) {
              const latest = this.runs[0];
              const run = latest.run;
              run.builds = latest.builds;
              this.run = run;
              this.runId = this.run.id;

              // Open the latest run by default if none are open
              if (Object.keys(this.openRuns).length === 0) {
                this.$set(this.openRuns, this.run.id, true);
              }

              // Populate global config from current/last run
              this.globalConfig.node_version_id = this.run.node_version_id || '';
              this.globalConfig.java_version = this.run.java_version || '';
              this.globalConfig.source_repository_id = this.run.source_repository_id || '';
              this.$nextTick(() => {
                this.globalConfig.source_branch_id = this.run.source_branch_id || '';
              });
              this.globalConfig.ios_version = this.run.ios_version || '';
              this.globalConfig.android_version = this.run.android_version || '';
              this.globalConfig.whats_new = this.run.whats_new || '';

              this.platforms.ios = this.run.platforms ? this.run.platforms.includes('ios') : true;
              this.platforms.android = this.run.platforms ? this.run.platforms.includes('android') : true;
              this.includePublishing = !!this.run.include_publishing;
            }
          }).catch(() => {
            this.runs = [];
            this.run = null;
            this.runId = null;
          });
    },
    pollStatuses() {
      // If runId is present poll run-specific endpoint to receive aggregated data
      if (this.runId) {
        window.axios.post('/backend/mass-update/run/status', {run_id: this.runId})
            .then((response) => {
              const run = response.data.run || null;
              if (run && response.data.builds) {
                run.builds = response.data.builds;

                // Update the run in our runs array
                const runIndex = this.runs.findIndex(r => r.run.id === run.id);
                if (runIndex !== -1) {
                  this.$set(this.runs[runIndex], 'run', run);
                  this.$set(this.runs[runIndex], 'builds', response.data.builds);
                } else {
                  this.runs.unshift({run: run, builds: response.data.builds});
                }
              }
              this.run = run;
              const builds = response.data.builds || [];
              builds.forEach((rb) => {
                // find configuration by app_configuration_id and update current_build if matches
                const config = this.configurations.find((c) => c.id === rb.app_configuration_id);
                if (config) {
                  config.current_build_id = rb.build_id;
                  config.current_build = Object.assign({}, config.current_build || {}, {
                    id: rb.build_id,
                    app_configuration_id: rb.app_configuration_id,
                    state: rb.state,
                    state_text: rb.state_text || config.current_build?.state_text,
                    codemagic_build_id: rb.codemagic_build_id,
                    branch_to_build: rb.branch_to_build,
                    started_at: rb.started_at,
                    finished_at: rb.finished_at,
                    log: rb.log,
                  });
                }
              });

              // Stop polling if run is no longer running
              if (this.run && this.run.status !== 'running') {
                this.stopPolling();
              }
            }).catch((error) => {
          this.errorMessage = error.response?.data?.message || error.message;
        });
        return;
      }

      const buildIds = this.configurations
          .map((config) => config.current_build_id)
          .filter((id) => !!id);

      if (buildIds.length === 0) {
        return;
      }

      window.axios.post('/backend/mass-update/status', {
        build_ids: buildIds,
      }).then((response) => {
        const builds = response.data.builds || [];
        builds.forEach((build) => {
          const config = this.configurations.find((c) => c.id === build.app_configuration_id);
          if (config) {
            config.current_build = build;
          }
        });
      }).catch((error) => {
        this.errorMessage = error.response?.data?.message || error.message;
      });
    },
    currentBuild(config) {
      return config.current_build || null;
    },
    isRunning(state) {
      return state === 15 || (state >= 1 && state <= 8);
    },
    isFinished(state) {
      return state === 9;
    },
    isFailed(state) {
      return state >= 10 && state <= 14;
    },
    statusBadgeClass(state) {
      if (this.isFinished(state)) {
        return 'label label-success';
      }
      if (this.isFailed(state)) {
        return 'label label-danger';
      }
      if (this.isRunning(state)) {
        return 'label label-warning';
      }
      return 'label label-default';
    },
    buildLog(config) {
      const build = this.currentBuild(config);
      if (!build || !build.log) {
        return this.labels.noLog;
      }
      return build.log;
    },
    toggleLog(config) {
      this.$set(this.logOpen, config.id, !this.showLog(config));
    },
    showLog(config) {
      return !!this.logOpen[config.id];
    },
    stopCurrentRun() {
      if (!this.runId) {
        this.errorMessage = 'Kein aktives Massenupdate zum Stoppen vorhanden.';
        return;
      }

      if (!confirm('Möchten Sie das Massenupdate wirklich stoppen? Alle in der Warteschlange befindlichen und laufenden Builds werden abgebrochen.')) {
        return;
      }

      window.axios.post('/backend/mass-update/run/stop', {run_id: this.runId})
          .then((response) => {
            this.run.status = 'stopped';
            this.run.finished_at = response.data.run.finished_at;
            // Stop polling immediately after stop
            this.stopPolling();
            // Refresh data one more time to ensure UI is up to date
            this.pollStatuses();
          })
          .catch((error) => {
            this.errorMessage = error.response?.data?.message || 'Massenupdate konnte nicht gestoppt werden: ' + error.message;
          });
    },
    getRunProgress(runData) {
      // Accept either a runData object {run, builds} or a bare run object
      const run = runData && runData.run ? runData.run : runData;
      const builds = runData && runData.builds ? runData.builds : (run && run.builds ? run.builds : null);

      // For a finished or stopped run, use server-side completed/total if available
      if (run && (run.status === 'finished' || run.status === 'stopped' || run.status === 'failed')) {
        if (run.total_builds > 0) {
          return Math.round(((run.completed_builds || run.total_builds) / run.total_builds) * 100);
        }
        return 100;
      }

      if (!builds || builds.length === 0) return 0;

      const total = builds.length;
      const sum = builds.reduce((acc, b) => {
        // Finished → 100%, failed/cancelled → 100% (they are done, just not successfully)
        if (b.state === 9 || (b.state >= 10 && b.state <= 14)) return acc + 100;
        // Waiting → 0%
        if (b.state === 0) return acc + 0;
        // Actively running: use the build's own progress field
        return acc + (b.progress || 0);
      }, 0);

      return Math.round(sum / total);
    },
    toggleRun(runId) {
      this.$set(this.openRuns, runId, !this.openRuns[runId]);
    },
    getNodeVersion(id) {
      const version = this.nodeVersions.find(nv => nv.id === id);
      return version ? version.version : id;
    },
    getRunRepositoryName(run) {
      if (run.source_repository_name) return run.source_repository_name;
      if (run.source_repository_id) {
        const repo = this.repositories.find(r => r.id == run.source_repository_id);
        return repo ? repo.name : null;
      }
      return null;
    },
    getRunBranchName(run) {
      if (run.source_branch_name) return run.source_branch_name;
      if (run.source_branch_id) {
        const branch = this.branches.find(b => b.id == run.source_branch_id);
        return branch ? branch.name : null;
      }
      return null;
    },
    getRunNodeVersionName(run) {
      if (run.node_version_name) return run.node_version_name;
      if (run.node_version_id) {
        return this.getNodeVersion(run.node_version_id);
      }
      return null;
    },
  },
};
</script>

<style scoped>
@keyframes blink {
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: 0.15;
  }
}

@keyframes pollingPulse {
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: 0.55;
  }
}

.mass-update-polling-pulse {
  animation: pollingPulse 2s ease-in-out infinite;
}
</style>